
import React from 'react';
import { CircuitMode, ComponentState } from '../types';

interface Props {
  mode: CircuitMode;
  state: ComponentState;
}

const CircuitView: React.FC<Props> = ({ mode, state }) => {
  const { isClosed, voltage, resistance1, resistance2 } = state;
  
  // Physics calculations
  const totalResistance = mode === CircuitMode.SERIES 
    ? (resistance1 + resistance2) 
    : (1 / (1/resistance1 + 1/resistance2));
  
  const current = isClosed ? (voltage / totalResistance) : 0;
  
  // Animation speed based on current
  const flowSpeed = current > 0 ? Math.max(0.1, 2 / current) : 0;

  return (
    <div className="w-full h-full max-w-2xl flex items-center justify-center">
      <svg viewBox="0 0 400 300" className="w-full h-auto drop-shadow-xl">
        {/* Background Grids */}
        <defs>
          <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
            <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#f1f5f9" strokeWidth="1"/>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" />

        {/* Wire paths */}
        <g stroke="#334155" strokeWidth="3" fill="none">
          {mode === CircuitMode.SERIES ? (
            <path d="M 100 200 L 100 100 L 300 100 L 300 200 L 100 200" />
          ) : (
            <>
              <path d="M 100 200 L 100 100 L 150 100" />
              <path d="M 250 100 L 300 100 L 300 200 L 100 200" />
              {/* Top Branch */}
              <path d="M 150 100 L 150 60 L 250 60 L 250 100" />
              {/* Bottom Branch */}
              <path d="M 150 100 L 150 140 L 250 140 L 250 100" />
            </>
          )}
        </g>

        {/* Electron Flow Animation */}
        {isClosed && (
          <g stroke="#facc15" strokeWidth="3" fill="none" className="electron-flow" style={{ animationDuration: `${flowSpeed}s` }}>
             {mode === CircuitMode.SERIES ? (
              <path d="M 100 200 L 100 100 L 300 100 L 300 200 L 100 200" />
            ) : (
              <>
                <path d="M 100 200 L 100 100 L 150 100" />
                <path d="M 250 100 L 300 100 L 300 200 L 100 200" />
                <path d="M 150 100 L 150 60 L 250 60 L 250 100" />
                <path d="M 150 100 L 150 140 L 250 140 L 250 100" />
              </>
            )}
          </g>
        )}

        {/* Battery */}
        <g transform="translate(180, 190)">
          <rect x="0" y="0" width="40" height="20" rx="2" fill="#ef4444" />
          <rect x="40" y="5" width="5" height="10" rx="1" fill="#ef4444" />
          <text x="20" y="15" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">BATTERY</text>
        </g>

        {/* Switch */}
        <g transform="translate(80, 130)">
          <circle cx="20" cy="20" r="4" fill="#334155" />
          <circle cx="60" cy="20" r="4" fill="#334155" />
          <line 
            x1="20" y1="20" 
            x2={isClosed ? 60 : 50} 
            y2={isClosed ? 20 : 0} 
            stroke="#334155" strokeWidth="4"
            className="transition-all duration-300"
          />
        </g>

        {/* Load 1 (Bulb/Resistor) */}
        <g transform={mode === CircuitMode.SERIES ? "translate(150, 90)" : "translate(180, 50)"}>
          <circle cx="20" cy="10" r="15" fill={isClosed ? "#fef08a" : "#f1f5f9"} stroke="#334155" className={isClosed ? "glow" : ""} />
          <text x="20" y="14" textAnchor="middle" fontSize="10" fill="#334155">R1</text>
        </g>

        {/* Load 2 */}
        <g transform={mode === CircuitMode.SERIES ? "translate(210, 90)" : "translate(180, 130)"}>
          <circle cx="20" cy="10" r="15" fill={isClosed ? "#fef08a" : "#f1f5f9"} stroke="#334155" className={isClosed ? "glow" : ""} />
          <text x="20" y="14" textAnchor="middle" fontSize="10" fill="#334155">R2</text>
        </g>
      </svg>
    </div>
  );
};

export default CircuitView;
